import { useContext, useState, useMemo, useEffect } from 'react';
import {
  MenuItem,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  Avatar,
  FormControl,
  InputLabel,
  Autocomplete,
  TextField,
  Alert,
  Box,
  Fab,
  Tooltip
} from '@mui/material';

import {
  MaterialReactTable,
  // createRow,
  useMaterialReactTable,
} from 'material-react-table';

import {
  ShopifyIcon
} from 'components/Icons';

import { 
  AuthContext, 
  export_items_to_shopify, 
  stopPropagationForTab, 
  availableCategoryGroups, 
  DEFAULT_SHOPIFY_SETTINGS, 
  SHOPIFY_TITLE_SECTION_SEPARATOR, 
  SHOPIFY_TITLE_TYPE_SEPARATOR,
  capitalizeFirstLetters
} from 'components';
import { useLocalStorage } from 'hooks/use-local-storage'
import { photo_url } from 'config';

const SHOPIFY_PRODUCT_EXPORT_LIMIT = 40


export const BulkModal = (props) => {
  const { items, onClose, selected } = props;
  const [ open, setOpen ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ error, setError ] = useState(false);
  const { account, accountId, brands, collections, userCreatedBrands, accountIntegrations, selectedStore, selectedSeason, customCategories, notify } = useContext(AuthContext);
  const [ selectedShopifyStore, setSelectedShopifyStore ] = useLocalStorage('shopify-import-shopify-store', accountIntegrations?.shopify_v2[Object.keys(accountIntegrations?.shopify_v2)[0]]?.name || "")
  const [ editedItems, setEditedItems ] = useState([]);

  useEffect(() => { 
    let shopify_filter_options = [
      { text: 'Sent to Shopify', value: 'yes' },
      { text: 'Not in Shopify', value: 'no' },
    ]

    let shopify_stores = Object.keys(accountIntegrations?.shopify_v2 || {})
    if (shopify_stores.length === 1) {
      setSelectedShopifyStore(shopify_stores[0])
    }
  }, [accountIntegrations])

  useEffect(() => {
    const new_items = items.map(i => {
      const brand = userCreatedBrands?.[i?.brand]?.displayName || brands?.[i?.brand]?.displayName || ''
      const department = availableCategoryGroups?.[i?.categoryType] || ''
      const category = customCategories[i?.categoryId]?.category || ''
      const collection = collections[i?.collectionId]?.displayName || ''
      const sname = i?.styleName || ''

      let title = ''
      if (selectedShopifyStore) {
        let settings = accountIntegrations?.shopify_v2?.[selectedShopifyStore]?.settings || DEFAULT_SHOPIFY_SETTINGS
        if (settings) {
          let title_config = settings?.title || "";
          title_config.split(SHOPIFY_TITLE_SECTION_SEPARATOR).forEach((section) => {
            const [type, value] = section.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
            switch(type) {
              case 'text':
                title = `${title.trim()} ${value}`;
                break;
                case 'prop':
                  title = `${title.trim()} `;
                  switch(value) {
                    case 'brand':
                      title += brand
                      break;
                    case 'category':
                      title += category
                      break;
                    case 'style_number':
                      title += i?.styleNumber || ''
                      break;
                    case 'style_name':
                      title += sname
                      break;
                    case 'department':
                      title += department
                      break;
                    case 'collection':
                      title += collection
                      break;
                    default:
                      break;
                  }
                  break;
            }
          })
        }

        title = capitalizeFirstLetters(title.trim())
      }

      return {
        id: i.id,
        photos: i.photos,
        color: i.color,
        note: i.note,
        category,
        categoryId: i.categoryId,
        categoryType: i.categoryType,
        vendor: brand,
        cost: i?.priceInfo?.price || 0,
        retail: i?.priceInfo?.retailPrice || 0,
        quantity: i?.priceInfo?.quantity || 0,
        title: title || '-'
      }
    })
    // TODO: Monkey patch to force re-render data. This has to go
    if (!loading) {
      setEditedItems([])
      setTimeout(() => { setEditedItems([...new_items]) }, 1)
    }
  }, [items, open])

  useEffect(() => {
    const new_items = editedItems.map((i, index) => {
      const brand = userCreatedBrands?.[items[index]?.brand]?.displayName || brands?.[items[index]?.brand]?.displayName || ''
      const department = availableCategoryGroups?.[items[index]?.categoryType] || ''
      const category = customCategories[items[index]?.categoryId]?.category || ''
      const collection = collections[items[index]?.collectionId]?.displayName || ''
      const sname = items[index]?.styleName || ''

      let title = ''
      if (selectedShopifyStore) {
        let settings = accountIntegrations?.shopify_v2?.[selectedShopifyStore]?.settings || DEFAULT_SHOPIFY_SETTINGS
        if (settings) {
          let title_config = settings?.title || "";
          // console.log('title_config', title_config)
          title_config.split(SHOPIFY_TITLE_SECTION_SEPARATOR).forEach((section) => {
            const [type, value] = section.split(SHOPIFY_TITLE_TYPE_SEPARATOR);
            switch(type) {
              case 'text':
                title = `${title.trim()} ${value}`;
                break;
                case 'prop':
                  title = `${title.trim()} `;
                  switch(value) {
                    case 'brand':
                      title += brand
                      break;
                    case 'category':
                      title += category
                      break;
                    case 'style_number':
                      title += i?.styleNumber || ''
                      break;
                    case 'style_name':
                      title += sname
                      break;
                    case 'department':
                      title += department
                      break;
                    case 'collection':
                      title += collection
                      break;
                    default:
                      break;
                  }
                  break;
            }
          })
        }

        title = title.trim()
      }

      return {
        ...i,
        title: title || '-'
      }
    })
    // TODO: Monkey patch to force re-render data. This has to go
    if (!loading) {
      setEditedItems([])
      setTimeout(() => { setEditedItems([...new_items]) }, 1)
    }
  }, [selectedShopifyStore])

  const columns = useMemo(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Id',
        enableEditing: false,
      },
      { 
        id: 'image', 
        accessorKey: 'photos',
        header: 'Image', 
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        enableColumnFilterModes: false,
        enableEditing: false,
        size: 30,
        Cell: ({ cell }) => (
          <Avatar variant="square" src={cell.getValue() ? `${photo_url}/o/${accountId}%2F${cell.getValue()[0]}.jpg?alt=media` : ""} />
        )
      },
      {
        accessorKey: 'title',
        header: 'Title',
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          multiline: 'true',
          required: false,
          inputProps: {
            sx: {
                overflow: 'scroll!important',
                maxHeight: 100,
            },
          },
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value.replaceAll('\n', ' ')
            setEditedItems([...updatedItems]);
          },
        }),
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          required: false,
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value
            setEditedItems([...updatedItems]);
          },
        }),
      },
      {
        id: 'category',
        accessorKey: 'category',
        header: 'Type',
        // muiEditTextFieldProps: ({ cell, row }) => ({
        //   type: 'text',
        //   required: false,
        //   onBlur: (event) => {
        //     let updatedItems = editedItems
        //     updatedItems[row.index][cell.column.id] = event.target.value
        //     setEditedItems([...updatedItems]);
        //   },
        // }),
        Edit: ({ cell, row }) => {
          return <Autocomplete
            value={customCategories[row.original.categoryId]}
            options={Object.keys(customCategories).map(e => customCategories[e]).filter(e => e.category && account.acctStores[selectedStore].departments.indexOf(parseInt(e.category_group_id)) !== -1)}
            groupBy={(option) => option.category_group}
            getOptionLabel={(e) => {
              return e.category || ""
            }}
            freeSolo
            renderInput={(params) => (
              <TextField 
                {...params} 
                sx={{ minWidth: '230px' }}
                InputLabelProps={{ shrink: true }} 
                label="Category (Product type in Shopify)" 
              />
            )}
            onChange={(e, value) => {
              let updatedItems = editedItems
              updatedItems[row.index]['category'] = value?.category
              updatedItems[row.index]['categoryId'] = value?.category_id
              updatedItems[row.index]['categoryType'] = value?.category_group
              setEditedItems([...updatedItems]);
            }}
          />
          // return <Category category={row.original.categoryId} onChange={(value) => { 
          //   let updatedItems = editedItems
          //   updatedItems[row.index][cell.column.id] = customCategories[value.categoryId].category
          //   setEditedItems([...updatedItems]);
          // }} />
        }
      },
      {
        accessorKey: 'cost',
        header: 'Cost',
        size: 30,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          required: false,
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value
            setEditedItems([...updatedItems]);
          },
        }),
      },
      {
        accessorKey: 'retail',
        header: 'Retail',
        size: 30,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          required: false,
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value
            setEditedItems([...updatedItems]);
          },
        }),
      },
      {
        accessorKey: 'quantity',
        header: 'Quantity',
        size: 30,
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'number',
          required: false,
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value
            setEditedItems([...updatedItems]);
          },
        }),
      },
      {
        accessorKey: 'note',
        header: 'Description',
        muiEditTextFieldProps: ({ cell, row }) => ({
          type: 'text',
          multiline: true,
          required: false,
          inputProps: {
            sx: {
                overflow: 'scroll!important',
                maxHeight: 100,
            },
          },
          onBlur: (event) => {
            let updatedItems = editedItems
            updatedItems[row.index][cell.column.id] = event.target.value
            setEditedItems([...updatedItems]);
          },
        }),
      },
    ],
    [editedItems]
  )

  const table = useMaterialReactTable({
    columns,
    data: editedItems,
    editDisplayMode: 'table',
    enableEditing: true,
    initialState: { columnVisibility: { id: false } }
  })

  const handleOpen = () => {
    if (onClose) {
      onClose();
    }

    if (items.length > SHOPIFY_PRODUCT_EXPORT_LIMIT) {
      notify({
        message: `Please select no more than ${SHOPIFY_PRODUCT_EXPORT_LIMIT} products to send to Shopify at one time.`,
        variant: 'warning'
      })
    } else {
      setOpen(true);
    }
  }
  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    setOpen(false);
  }

  const handleSave = () => {
    setLoading(true);
    notify({ message: 'Your product(s) are being uploaded to Shopify and will appear in a moment', variant: 'info' });
    let data = { 
      // "shopify_store": "faves-demo-emil-2",
      "shopify_store": selectedShopifyStore,
      "store": selectedStore,
      "season": selectedSeason,
      "items": editedItems.slice(0, SHOPIFY_PRODUCT_EXPORT_LIMIT)
      // "items": editedItems.slice(0, SHOPIFY_PRODUCT_EXPORT_LIMIT).map(e => ({ "id": e.id})),
    }
    export_items_to_shopify(data).then((result) => {
      setLoading(false);
      const data = result.data;
      if (data.errors === 0) {
        notify({ message: `${data.products_uploaded} product(s) have been succesfully uploaded to Shopify` });
        handleClose()
      } else {
        // This may need to change
        data?.error_messages?.forEach((message) => {
          notify({ message: message, variant: 'error' });
        })
        
        notify({ message: `There was an error sending ${data.errors} product(s) to Shopify`, variant: 'error' });
        console.log('data.errors', data.errors)
        setError(data.errors);
      }
    }).catch((error) => {
      console.log('error', error)
      setLoading(false);
      setError(true);
      notify({ message: 'There was an error sending the products to Shopify', variant: 'error' });
    });
  }
  
  return (
    <>
      <Tooltip onClick={handleOpen} title="Send to Shopify" disabletriggerfocus="true" disablerestorefocus="true" placement='bottom' slotProps={{ popper: { modifiers: [ { name: 'offset', options: { offset: [0, 20] } } ] } }}>
        <span>
          <Fab disableRipple disabled={!selected} color="secondary">
            <ShopifyIcon sx={{ 
              width: '1.1em',
              height: '1.1em',
              position: 'relative',
              top: '-1px'
            }} />
          </Fab>
        </span>
      </Tooltip>
      <Dialog className='shopify-import-dialog' onKeyDown={stopPropagationForTab} open={open} onClose={handleClose} fullWidth>
        {error !== false ? (
          <Box>
            <Alert onClose={() => { setError(false) }} sx={{ justifySelf: 'center', fontSize: '16px' }} severity="error">
              {error === true ? (
                <>There was an error sending the products to Shopify. Please try again or contact <a _target="blank" href="mailto:support@thefavesapp.com">FAVES Support</a>.</>
              ) : (
                <>There was an error sending {error} product(s) to Shopify. Please try again or contact <a _target="blank" href="mailto:support@thefavesapp.com">FAVES Support</a>.</>
              )}
            </Alert>
          </Box>
        ) : null}
        <DialogTitle>Review & edit your items before sending to Shopify</DialogTitle>
        <DialogContent>
          <MaterialReactTable table={table} />
        </DialogContent>
        <DialogActions>
          <FormControl sx={{ width: 200 }}>
            <InputLabel>Select Shopify Store</InputLabel>
            <Select
              value={selectedShopifyStore}
              onChange={(e) => {
                setSelectedShopifyStore(e.target.value)
              }}
              label="Select Shopify Store"
            >
              {Object.keys(accountIntegrations?.shopify_v2||{}).map(e => (
                <MenuItem key={e} value={e}>{accountIntegrations?.shopify_v2[e]?.name || e}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" color="secondary" onClick={handleClose}>Cancel</Button>
          <Button variant="outlined" color="secondary" disabled={!selectedShopifyStore || loading} onClick={() => { setTimeout(handleSave, 200) }}>Send</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}